import { ensureCollabShare, fetchCollabProject, inviteCollabAccount, inviteCollabEmail, leaveCollab, removeCollabUser, searchAccounts, setCollabGlobalRole, setCollabRole, type CollabProject, type CollabRole } from '../../state/catalogApi';
import { plainLook } from '../participantColors';
import type { PresenceSource } from '../presenceSource';
import { MIN_SEARCH, type LinkAccess, type ShareFailure, type ShareInvitee, type ShareMatch, type SharePerson, type ShareRole, type ShareSource, type ShareState } from './source';

export interface CatalogShareDeps {
  projectId: string;
  /** The invitation's token the room was opened with: it proves the link to the catalog. */
  linkToken: string;
  token: () => string | null;
  presence: Pick<PresenceSource, 'get' | 'subscribe' | 'look'>;
}

const LINK: Record<CollabRole, LinkAccess> = { no_access: 'invited', can_view: 'view', can_edit: 'edit', owner: 'edit' };
const GLOBAL_ROLE = { invited: 'no_access', view: 'can_view', edit: 'can_edit' } as const satisfies Record<LinkAccess, CollabRole>;
const roleOf = (role: CollabRole): ShareRole => role === 'owner' ? 'owner' : role === 'can_edit' ? 'editor' : 'viewer';
const failureOf = (error: unknown): ShareFailure => {
  const code = (error as { code?: string } | null)?.code;
  return code === 'gone' || code === 'forbidden' ? 'gone' : code === 'signed-out' ? 'signed-out' : 'failed';
};

const unsupported = () => Object.assign(new Error('no route'), { code: 'forbidden' });

/** The Share dialog in the app's live room: the catalog's `/collab` answer, and the link visitors who are in the room now. */
export class CatalogShare implements ShareSource {
  private answer: CollabProject | null = null;
  private online = '';
  private state: ShareState = { status: 'loading', error: null, mode: 'catalog', myRole: 'viewer', people: [], link: 'invited', noLink: 'member', requests: [], editRequest: null };
  private readonly listeners = new Set<() => void>();
  private readonly stopPresence: () => void;
  private request: AbortController | null = null;

  constructor(private readonly deps: CatalogShareDeps) {
    this.online = this.onlineKey();
    this.stopPresence = deps.presence.subscribe(() => {
      const online = this.onlineKey();
      if (online === this.online) return;
      this.online = online;
      if (this.answer) this.publish({ ...this.state, people: this.people(this.answer) });
    });
  }

  get = (): ShareState => this.state;

  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };

  async refresh(): Promise<void> {
    this.request?.abort();
    const token = this.deps.token();
    if (!token) { this.publish({ ...this.state, status: 'error', error: 'signed-out' }); return; }
    if (this.state.status === 'error') this.publish({ ...this.state, status: 'loading', error: null });
    const request = this.request = new AbortController();
    try {
      const answer = await fetchCollabProject(this.deps.projectId, token, this.deps.linkToken, request.signal);
      if (request.signal.aborted) return;
      this.answer = answer;
      this.publish({ status: 'ready', error: null, mode: 'catalog', myRole: roleOf(answer.myRole), people: this.people(answer), link: LINK[answer.globalRole],
        noLink: answer.shareUrl || answer.myRole === 'owner' ? null : 'member', requests: [], editRequest: null });
    } catch (error) {
      if (request.signal.aborted) return;
      this.publish({ ...this.state, status: 'error', error: failureOf(error) });
    } finally {
      if (this.request === request) this.request = null;
    }
  }

  load(): Promise<void> { return this.state.status === 'ready' || this.request ? Promise.resolve() : this.refresh(); }

  async linkUrl(): Promise<string> {
    const answer = this.answer;
    if (answer?.shareUrl) return answer.shareUrl;
    const token = this.deps.token();
    if (answer?.myRole !== 'owner' || !token) throw Object.assign(new Error('no link'), { code: token ? 'forbidden' : 'signed-out' });
    return (await ensureCollabShare(this.deps.projectId, token)).shareUrl;
  }

  setLink(link: LinkAccess): Promise<void> { return setCollabGlobalRole(this.deps.projectId, this.signedIn(), GLOBAL_ROLE[link]); }

  setRole(id: string, role: 'editor' | 'viewer'): Promise<void> {
    return setCollabRole(this.deps.projectId, this.signedIn(), id, role === 'editor' ? 'can_edit' : 'can_view');
  }

  /** An invitation nobody signed in with yet is listed as `email:<address>` and removed by that address. */
  remove(id: string): Promise<void> { return removeCollabUser(this.deps.projectId, this.signedIn(), id.startsWith('email:') ? id.slice(6) : id); }

  async search(term: string, signal?: AbortSignal): Promise<ShareMatch[]> {
    const wanted = term.trim();
    return wanted.length < MIN_SEARCH ? [] : searchAccounts(wanted, this.signedIn(), signal);
  }

  async invite(invitee: ShareInvitee, role: 'editor' | 'viewer'): Promise<void> {
    const token = this.signedIn(), grant = role === 'editor' ? 'can_edit' : 'can_view';
    if ('id' in invitee) await inviteCollabAccount(this.deps.projectId, token, invitee.id, grant);
    else await inviteCollabEmail(this.deps.projectId, token, invitee.email, grant);
    await this.refresh();
  }

  /** Requests have no route on the catalog yet (docs/tasks/share-access-requests): there is none to answer or send. */
  async approve(): Promise<void> { throw unsupported(); }
  async deny(): Promise<void> { throw unsupported(); }
  async requestEdit(): Promise<void> { throw unsupported(); }

  async leave(): Promise<void> { await leaveCollab(this.deps.projectId, this.signedIn()); }

  dispose(): void {
    this.request?.abort();
    this.stopPresence();
    this.listeners.clear();
  }

  private signedIn(): string {
    const token = this.deps.token();
    if (!token) throw Object.assign(new Error('signed-out'), { code: 'signed-out' });
    return token;
  }

  private onlineKey(): string {
    return [...new Set(this.deps.presence.get().participants.map(person => person.userId))].sort().join('\n');
  }

  private people(answer: CollabProject): SharePerson[] {
    const online = new Set(this.online.split('\n')), me = answer.me.accountId, manager = answer.myRole === 'owner';
    const names = new Map(this.deps.presence.get().participants.map(participant => [participant.userId, participant.displayName]));
    const row = (id: string, name: string, role: ShareRole, { avatar, ...more }: Partial<SharePerson> & { avatar?: string } = {}): SharePerson => ({
      id, name, role, self: !!id && id === me, pending: false, online: id === me || online.has(id), visitor: false, manageable: false,
      look: this.deps.presence.look?.(id) ?? plainLook(id || name, role, avatar), ...more,
    });
    const rows = [row(answer.owner.accountId, answer.owner.name, 'owner', { avatar: answer.owner.avatar })];
    for (const member of answer.members) rows.push(row(member.accountId ?? `email:${member.email ?? ''}`, member.name || member.email || '', roleOf(member.role),
      { avatar: member.avatar, ...(member.email ? { email: member.email } : {}), pending: member.status !== 'active', manageable: manager, ...(member.accountId ? {} : { fixedRole: true }) }));
    const listed = new Set(rows.map(person => person.id)), linkRole: ShareRole = answer.globalRole === 'can_edit' ? 'editor' : 'viewer';
    const visitors = [...new Set([...online, me])].filter(id => id && !listed.has(id));
    for (const id of visitors) rows.push(row(id, id === me ? answer.me.name : names.get(id) ?? '', linkRole, { visitor: true, online: true }));
    return rows;
  }

  private publish(state: ShareState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }
}
